/**
 * Read-only roof-scope section for the estimate detail page (PRP-ROOF-2 S3).
 *
 * Renders the roof scope persisted at `scope_data.metadata.roof` — the same
 * blob the ESX exporter reads to emit the Roof folder. Self-contained (no
 * TreeRail/override machinery) so it render-tests without mounting the page.
 */

import { activitySymbol } from '../../lib/semantics';

/** Narrowed shape of one persisted roof line item (tolerant of missing fields). */
interface RoofLine {
  cat?: string;
  sel?: string;
  dsc?: string;
  unit?: string;
  activity?: string | null;
  quantity?: number;
  section?: string;
  review_required?: boolean;
}

/** Pull the line-item array out of the loosely-typed roof blob, or []. */
function readLines(roof: Record<string, unknown> | null | undefined): RoofLine[] {
  const result = (roof as { result?: unknown })?.result as { line_items?: unknown } | undefined;
  const items = result?.line_items;
  return Array.isArray(items) ? (items as RoofLine[]) : [];
}

export interface RoofScopeSectionProps {
  roof: Record<string, unknown> | null | undefined;
}

/**
 * Renders the "Roof" section when the roof blob carries line items; renders
 * nothing otherwise (no roof scope, or an error blob with no result).
 */
export function RoofScopeSection({ roof }: RoofScopeSectionProps) {
  const lines = readLines(roof);
  if (lines.length === 0) return null;

  const profileId = (roof as { profile_id?: unknown })?.profile_id;
  const damageMode = ((roof as { result?: { damage_mode?: unknown } })?.result?.damage_mode ??
    '') as string;

  return (
    <section data-testid="roof-scope-section" className="border-t border-[var(--bd-subtle)] px-[18px] py-4">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-[13px] font-[560] uppercase tracking-wide text-[var(--tx-lo)]">Roof</h2>
        {typeof profileId === 'string' && profileId && (
          <span className="rounded-full bg-[var(--accent-soft)] px-[8px] py-[2px] font-[var(--font-mono)] text-[10.5px] font-[560] text-[var(--accent-ink)]">
            {profileId}
          </span>
        )}
        {damageMode && (
          <span className="text-[11px] text-[var(--tx-faint)]">
            {damageMode.replace(/_/g, ' ')}
          </span>
        )}
        <span className="ml-auto text-[11px] text-[var(--tx-faint)]">
          {lines.length} line item{lines.length === 1 ? '' : 's'}
        </span>
      </div>

      <ul className="flex flex-col gap-[6px]">
        {lines.map((li, i) => (
          <li
            key={`${li.cat ?? ''}-${li.sel ?? ''}-${i}`}
            className="flex items-start gap-[10px] rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-1)] px-[10px] py-[7px]"
          >
            <span className="w-[16px] shrink-0 text-center font-[var(--font-mono)] text-[13px] text-[var(--tx-hi)]">
              {activitySymbol(li.activity || '')}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-[6px]">
                <span className="font-[var(--font-mono)] text-[11.5px] text-[var(--tx-mid)]">
                  {li.cat} {li.sel}
                </span>
                {li.review_required && (
                  <span className="rounded-full bg-[var(--warn-bg)] px-[7px] py-[1px] text-[10px] font-[560] uppercase tracking-[.04em] text-[var(--warn)]">
                    review
                  </span>
                )}
              </span>
              <span className="block text-[12.5px] text-[var(--tx-hi)]">{li.dsc}</span>
            </span>
            <span className="shrink-0 whitespace-nowrap font-[var(--font-mono)] text-[12px] text-[var(--tx-mid)]">
              {li.quantity} {li.unit}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default RoofScopeSection;
